import * as React from "react"
import { Box, Typography, Chip, Avatar, Tooltip, IconButton } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { createSkuDrawer, mapToSkuDrawer, imageModal } from "../@signals"


function getCurrencySymbol(currencyCode) {
    const currencyMap = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'CNY': '¥',
        'MXN': '$',
        'MEX': '$',
        'BRL': 'R$',
        'ARS': '$',
        'CLP': '$',
        'COP': '$',
        'PEN': 'S/',
        'CAD': 'C$',
        'AUD': 'A$',
        'CHF': 'CHF',
        'INR': '₹',
        'RUB': '₽',
        'KRW': '₩',
        'TRY': '₺',
        'ZAR': 'R',
        'SEK': 'kr',
        'NOK': 'kr',
        'DKK': 'kr',
        'PLN': 'zł',
        'THB': '฿',
        'IDR': 'Rp',
        'MYR': 'RM',
        'PHP': '₱',
        'SGD': 'S$',
        'NZD': 'NZ$',
        'HKD': 'HK$'
    }
    return currencyMap[currencyCode] || currencyCode || '$'
}


export const searchTextColumn = () => ({
    name: 'search_text',
    label: 'Search',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true,
        viewColumns: false
    }
})

export const productIdColumn = () => ({
    name: 'product_id',
    label: 'Product ID',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const idMappingColumn = () => ({
    name: 'id_mapping',
    label: 'Mapping ID',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: false
    }
})

export const eanColumn = () => ({
    name: 'ean',
    label: 'EAN',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const rawTitleColumn = () => ({
    name: 'raw_title',
    label: 'Raw Title',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const rawSubtitleColumn = () => ({
    name: 'raw_subtitle',
    label: 'Raw Subtitle',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const rawVariantColumn = () => ({
    name: 'raw_variant',
    label: 'Raw Variant',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const siteColumn = () => ({
    name: 'site',
    label: 'Site',
    options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {value || '-'}
            </Typography>
        )
    }
})

export const brandColumn = () => ({
    name: 'brand',
    label: 'Brand',
    options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1' }}>
                {value || '-'}
            </Typography>
        )
    }
})

export const batchColumn = () => ({
    name: 'batch_info',
    label: 'Batch',
    options: {
        filter: false,
        sort: true,
        sortCompare: (order) => (a, b) => {
            const dateA = new Date(a.data?.ts_finished || 0)
            const dateB = new Date(b.data?.ts_finished || 0)
            return order === 'asc' ? dateA - dateB : dateB - dateA
        },
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                {value?.id_batch && (
                    <Chip
                        size="small"
                        label={`#${value.id_batch}`}
                        sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#e0e7ff', color: '#4338ca' }}
                    />
                )}
                {value?.ts_finished && (
                    <Typography variant="caption" color="text.secondary">
                        {value.ts_finished}
                    </Typography>
                )}
            </Box>
        )
    }
})

export const rawProductColumn = () => ({
    name: 'raw',
    label: 'Product',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
            align: 'left',
            sx: { backgroundColor: '#fef3c7' }
        }),
        setCellProps: () => ({ align: 'left' }),
        customBodyRender: (raw) => {
            if (!raw) return '-'

            const handleImageClick = (e) => {
                e.stopPropagation()
                imageModal.value = {
                    title: raw?.title,
                    variantRaw: raw?.variant_raw,
                    image: raw?.image
                }
            }

            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar
                        src={raw?.image}
                        variant="rounded"
                        onClick={handleImageClick}
                        sx={{
                            width: 70,
                            height: 70,
                            bgcolor: '#f3f4f6',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            },
                            '& img': {
                                objectFit: 'contain'
                            }
                        }}
                    >
                        <IconSeeker icon="image" sx={{ color: '#9ca3af' }} />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                            <Tooltip title={raw?.title || '-'} placement="top">
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        wordBreak: 'break-word',
                                        lineHeight: 1.3
                                    }}
                                >
                                    {raw?.title || '-'}
                                </Typography>
                            </Tooltip>
                            {raw?.product_url && (
                                <Tooltip title="Open product page">
                                    <a
                                        href={raw.product_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 14, color: '#6366f1' }} />
                                    </a>
                                </Tooltip>
                            )}
                        </Box>
                        {raw?.subtitle && (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {raw.subtitle}
                            </Typography>
                        )}
                        {raw?.variant_raw && (
                            <Tooltip title={raw.variant_raw} placement="top">
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        display: 'inline',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: '#e0e7ff',
                                        color: '#4338ca',
                                        mt: 0.5,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        lineHeight: 1.8,
                                        boxDecorationBreak: 'clone',
                                        WebkitBoxDecorationBreak: 'clone'
                                    }}
                                >
                                    {raw.variant_raw}
                                </Typography>
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                ID: {raw?.product_id || '-'}
                            </Typography>
                            {raw?.ean && (
                                <Tooltip title="EAN">
                                    <Chip
                                        icon={<IconSeeker icon="barcode" sx={{ fontSize: 12 }} />}
                                        label={raw.ean}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Tooltip>
                            )}
                            {raw?.reference && (
                                <Tooltip title="Reference">
                                    <Chip
                                        icon={<IconSeeker icon="tag" sx={{ fontSize: 12 }} />}
                                        label={raw.reference}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        {raw?.actual_price > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#059669',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    {getCurrencySymbol(raw.currency)}{raw.actual_price.toFixed(2)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            )
        }
    }
})

export const iaInfoColumn = () => ({
    name: 'ia_info',
    label: 'IA Analysis',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
            align: 'left',
            sx: { backgroundColor: '#ede9fe' }
        }),
        setCellProps: () => ({ align: 'left' }),
        customBodyRender: (value) => {
            if (!value?.ia_text && !value?.ia_options) {
                return (
                    <Box sx={{ color: '#9ca3af', py: 1 }}>
                        <Typography variant="caption">No IA analysis</Typography>
                    </Box>
                )
            }

            return (
                <Box sx={{ maxWidth: 300 }}>
                    {value?.ia_text && (
                        <Tooltip title={value.ia_text} placement="top">
                            <Typography
                                variant="caption"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: '#6b7280'
                                }}
                            >
                                {value.ia_text}
                            </Typography>
                        </Tooltip>
                    )}
                    {value?.ia_options && value.ia_options.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {value.ia_options.length} option(s) suggested
                            </Typography>
                        </Box>
                    )}
                </Box>
            )
        }
    }
})

export const statusColumn = () => ({
    name: 'status',
    label: 'Status',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: () => (
            <Chip
                icon={<IconSeeker icon="circle-question" sx={{ fontSize: 14 }} />}
                label="Not Found"
                size="small"
                sx={{
                    bgcolor: '#fef2f2',
                    color: '#dc2626',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                }}
            />
        )
    }
})

export const actionsColumn = () => ({
    name: 'actions_data',
    label: 'Actions',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (actionsData) => {
            const { id_mapping, raw } = actionsData || {}

            const handleCreateSku = (e) => {
                e.stopPropagation()

                const candidate = {
                    id_mapping: id_mapping,
                    id_site: raw?.id_site,
                    product_id: raw?.product_id,
                    raw: raw
                }

                createSkuDrawer.value = {
                    candidates: [candidate],
                    prefill: {
                        id_brand: raw?.id_brand,
                        brand_name: raw?.brand
                    }
                }
            }

            const handleMapToSku = (e) => {
                e.stopPropagation()

                mapToSkuDrawer.value = {
                    raw: raw,
                    id_mapping: id_mapping
                }
            }

            return (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Map to existing SKU">
                        <IconButton
                            size="small"
                            onClick={handleMapToSku}
                            sx={{
                                color: '#6366f1',
                                '&:hover': {
                                    bgcolor: '#eef2ff',
                                    color: '#4f46e5'
                                }
                            }}
                        >
                            <IconSeeker icon="link" sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Create new SKU">
                        <IconButton
                            size="small"
                            onClick={handleCreateSku}
                            sx={{
                                color: '#059669',
                                '&:hover': {
                                    bgcolor: '#ecfdf5',
                                    color: '#047857'
                                }
                            }}
                        >
                            <IconSeeker icon="plus" sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    }
})
